import truncate from 'lodash/truncate';
import React, { ComponentPropsWithRef, FC } from 'react';
import { FacebookShareButton, LinkedinShareButton, TwitterShareButton } from 'react-share';
import { SocialNetwork } from '@actus/types';
import { FacebookLogoIcon } from '@components/icons/social/FacebookLogo.icon';
import { LinkedinLogoIcon } from '@components/icons/social/LinkedinLogo.icon';
import { TwitterLogoIcon } from '@components/icons/social/TwitterLogo.icon';
import { useTranslation } from '@locales/useTranslation.hook';
import { isServer } from '@utils/server';

const TWITTER_LENGTH = 280;

export type ShareProps = {
  socialNetwork: SocialNetwork;
  imgUrl?: string;
  imgAlt?: string;
  title?: string;
  // up to 200 chars
  description?: string;
  iconSize?: number;
};

type SharerProps = Omit<ShareProps, 'socialNetwork'> & { socialNetwork?: ShareProps['socialNetwork']; url: string };

type Props = ComponentPropsWithRef<'button'> & ShareProps;

const buildSocialIcon = ({
  socialNetwork,
  title,
  url,
  iconSize,
  ...rest
}: Omit<SharerProps, 'imgUrl'>): JSX.Element => {
  const hashtag = 'pleinchamp';
  switch (socialNetwork) {
    case SocialNetwork.Facebook: {
      return (
        <FacebookShareButton hashtag={`#${hashtag}`} quote={title} style={{ height: iconSize }} url={url} {...rest}>
          <FacebookLogoIcon size={iconSize} />
        </FacebookShareButton>
      );
    }
    case SocialNetwork.Twitter: {
      return (
        <TwitterShareButton hashtags={[hashtag]} style={{ height: iconSize }} title={title} url={url} {...rest}>
          <TwitterLogoIcon size={iconSize} />
        </TwitterShareButton>
      );
    }
    case SocialNetwork.Linkedin: {
      return (
        <LinkedinShareButton style={{ height: iconSize }} url={url} {...rest}>
          <LinkedinLogoIcon size={iconSize} />
        </LinkedinShareButton>
      );
    }
    case undefined:
    case SocialNetwork.Youtube: {
      return <></>;
    }
    default: {
      const switchComplete: never = socialNetwork;
      return switchComplete;
    }
  }
};

export const buildMetaTags = ({ imgUrl, title, url, description, imgAlt }: SharerProps): JSX.Element => {
  // Twitter : https://developer.twitter.com/en/docs/tweets/optimize-with-cards/overview/markup
  // Facebook : https://developers.facebook.com/tools/debug
  return (
    <>
      <meta key="fb:app_id" content="766998407408723" property="fb:app_id" />
      <meta key="og:type" content="article" property="og:type" />
      {title && <meta key="og:title" content={title} property="og:title" />}

      {imgUrl && <meta key="og:image" content={imgUrl} property="og:image" />}
      {imgUrl && imgAlt && <meta key="og:image:alt" content={imgAlt} property="og:image:alt" />}
      {description && <meta key="og:description" content={description} property="og:description" />}
      {url && <meta key="og:url" content={url} property="og:url" />}

      <meta key="twitter:card" content="summary" name="twitter:card" />
      {title && <meta key="twitter:title" content={title} name="twitter:title" />}
      {imgUrl && <meta key="twitter:image" content={imgUrl} name="twitter:image" />}
      {imgUrl && imgAlt && <meta key="twitter:image:alt" content={imgAlt} name="twitter:image:alt" />}
      {description && (
        <meta
          key="twitter:description"
          content={
            description.length > TWITTER_LENGTH
              ? truncate(description, { length: TWITTER_LENGTH, separator: /,? +/ })
              : description
          }
          name="twitter:description"
        />
      )}
      <meta key="twitter:site" content="@Pleinchamp" name="twitter:site" />
    </>
  );
};

const SocialShareButton: FC<Props> = ({ socialNetwork, title, imgUrl, imgAlt, iconSize, ...rest }) => {
  const { t } = useTranslation(['common']);
  if (isServer()) {
    return null;
  }
  const url = window.location.href;

  return <>{buildSocialIcon({ iconSize, socialNetwork, title: title || t('common.app-name'), url, ...rest })}</>;
};

export { SocialShareButton };
